import { useCallback, useEffect, useMemo, useState } from "react";
import { components } from '@api/api';
import { ID } from "@datassential/platform-ui-lib";
import { exportMenuExamples, fetchMenuExamples } from "@core/api";
import { iUseData, useData } from "@hooks/useData";
import http, { AbortablePromise } from "@core/http";
import apiConfig from "@apiConfig";
import { toast } from "react-toastify";


export type Response = components["schemas"]["MenuExamplesOutputModel"];
export type MenuExample = components["schemas"]["MenuItemExample"];
type Payload = components["schemas"]["MenuExamplesFilterModel"];

const TAKE = 100;
let request: AbortablePromise<Response> = new Promise(() => {
});

async function getMenuExamplesData(payload: Payload): Promise<Response> {
    if (request && request.abort) request.abort();

    request = fetchMenuExamples(payload);

    return request;
}

function useMenuExamplesData(payload: Payload): iUseData<Response> {
    let fn = getMenuExamplesData;
    if (payload.searchWord!.trim() === '' || payload.tableParameters!.take! <= 0) {
        fn = () => {
            return new Promise(() => {
            })
        };
    }
    return useData({
        totalRecords: undefined,
        data: [],
    }, fn, payload);
}

export interface iUseMenuExamplesParams {
    search: string;
    statesIds: ID[];
    selectedSegmentId: ID;
    includeChains: boolean;
    tableSearch: string;
    countryId: ID;
}

let requestExport: AbortablePromise | null = null;

export function useMenuExamples(params: iUseMenuExamplesParams) {
    const {
        search,
        statesIds,
        selectedSegmentId,
        includeChains,
        tableSearch,
        countryId,
    } = params;

    const [items, setItems] = useState<MenuExample[]>([]);
    const [isMainTermLocalKeyword, setIsMainTermLocalKeyword] = useState(false);
    const [mainTermKeywordId, setMainTermKeywordId] = useState<ID | null>(null);
    const [totalRecords, setTotalRecords] = useState<number | undefined>(undefined);
    const [skip, setSkip] = useState(0);
    const [isLoadingDebounced, setIsLoadingDebounced] = useState(false);

    const isLoaded = useMemo(
        () => totalRecords !== undefined && items.length >= totalRecords,
        [items, totalRecords]
    );

    const reset = useCallback(
        () => {
            setItems([]);
            setIsMainTermLocalKeyword(false);
            setMainTermKeywordId(null);
            setTotalRecords(undefined);
            setSkip(0);
        },
        []
    );

    const payload = useMemo(
        (): Payload => {
            return {
                includeChains,
                searchWord: search,
                segmentId: selectedSegmentId as number,
                states: statesIds as number[],
                countryCode: countryId as string,
                tableParameters: {
                    skip,
                    take: TAKE,
                    search: tableSearch,
                },
            };
        },
        [search, statesIds, selectedSegmentId, includeChains, tableSearch, countryId, skip]
    );

    const { isLoading, data } = useMenuExamplesData(payload);

    const inc = useCallback(
        () => {
            if (!isLoaded && !isLoading) {
                setSkip((skip) => skip + TAKE);
            }
        },
        [isLoaded, isLoading]
    );

    useEffect(() => {
        if (!isLoading) {
            setItems((items) => [...items, ...(data.data || [])]);
            setIsMainTermLocalKeyword(data.isLocal ?? false);
            setMainTermKeywordId(data.keywordId ?? null);
            setTotalRecords(data.totalRecords);
        }
    }, [data, isLoading]);

    useEffect(() => {
        reset();
    }, [params, reset]);

    useEffect(() => {
        setIsLoadingDebounced(isLoading);
    }, [isLoading]);

    const download = useCallback(
        async () => {
            toast.info("Downloading...");
            requestExport && requestExport.abort && requestExport.abort();
            requestExport = exportMenuExamples(payload);
            const token = await requestExport;

            http.downloadFileXHRFromUrl(
                'GET',
                apiConfig.get('export menu examples', { key: token })
            ).then(() => {
                toast.success("Download was successful");
            }).catch(err => {
                toast.error("Something went wrong");
            });
        },
        [payload]
    );

    return {
        isLoading: isLoadingDebounced,
        isLoaded,
        inc,
        data: items,
        isMainTermLocalKeyword,
        mainTermKeywordId,
        download,
    };
}